import React, { useState, useEffect, useRef, useMemo } from 'react';
import { companyUUID } from '@/service/api';
import { generageUrlFromPage, matchUrlParts, matchExactUrls } from '@/service/strategy/logic';
import { PATH_CHANGE_EVENT } from '@/utils/events';
import { getCompanyStaticUrl } from '@/utils';
import { SMALLRIG, STORAGE_KEY } from '@/constant/constant';
import { getSession, setLocal, removeLocal } from '@/utils/storage';
import { isCommonOrTest } from '@/utils/platform';
import { formEventV2 } from '@/service/formEventV2';
import { setIconCloseTime } from '../../service/strategy/priority';
import { clearIconWinner } from '@/service/strategy/iconTiming';
import {
  calcIconBoxSize,
  calcIconCloseSize,
  resolveIconClosePosition,
  hasIconCloseEnhance,
  calcIconCloseDockInset,
  isOriginalFitMode,
  getIconDockSide,
  buildIconOpenSizeCss,
  buildIconContentStyles,
  DEFAULT_ICON_CLOSE_COLOR,
  DEFAULT_ICON_CLOSE_BG_COLOR
} from '@/utils/iconFitSize';
// @ts-ignore
import { ReactComponent as CartxIcon } from '@/assets/imgs/close_custom.svg';
// 收起 icon 关闭：设计稿 icon-close（圆形底 + X），支持背景色/图标色
// @ts-ignore
import { ReactComponent as IconCloseSvg } from '@/assets/imgs/icon-close.svg';

const debug = require('debug')('cartsee:icon');

//光标距离元素上边界的距离
let elementTop;
let elementLeft;
let preventFlag = false;

let dragging = false;
let cloneEl = null;
let initial: any = {};
const getNumber = (num) => {
  const str = `${num}`.split('px');
  if (str[0].length > 0) {
    return Number(str[0]);
  } else {
    return 0;
  }
};

function IconModt(props) {
  const { position, show, setShow, triggerEventType, id, name, scheduleType, startTime, endTime, onExpire } = props;

  const [iconModtCss, setIconModtCss] = useState<any>({});
  const [urlShow, setUrlShow] = useState(true);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const ref = useRef(null);

  const boxSize = useMemo(() => {
    return calcIconBoxSize({
      iconSizeType: position?.iconSizeType,
      iconFitMode: position?.iconFitMode,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height
    });
  }, [position?.iconSizeType, position?.iconFitMode, naturalSize.width, naturalSize.height]);

  const closePosition = resolveIconClosePosition(position?.position, position?.iconClosePosition);
  // iconClose 开启即走增强关闭：优先已有配置，缺省再兜底
  const closeEnhance = hasIconCloseEnhance(position);
  const closeStyle = closeEnhance ? (position?.iconCloseStyle === 'inside' ? 'inside' : 'outside') : 'legacy';
  const closeSize = calcIconCloseSize(boxSize.width, boxSize.height, position?.iconCloseSize, !closeEnhance, position);
  const closeColor = position?.iconCloseColor || (closeEnhance ? DEFAULT_ICON_CLOSE_COLOR : 'rgba(0, 0, 0, 0.75)');
  const closeBgColor = position?.iconCloseBgColor || (closeEnhance ? DEFAULT_ICON_CLOSE_BG_COLOR : undefined);

  useEffect(() => {
    const handelPageChange = () => {
      // 侧边栏排除购物车结账（与线上一致：仅看 iconExcludePages，不跟随弹窗页面规则）
      let hostname = window?.location?.hostname;
      let pathname = window?.location?.pathname;
      let paramseters = window?.location?.search;

      const config = getSession(STORAGE_KEY.POPUP) || '{}';
      let rets = JSON.parse(config).rets || [];

      rets?.forEach((ret) => {
        if (ret.id === id && ret.settings?.targeting?.iconExcludePages) {
          if (isCommonOrTest() && [...SMALLRIG].includes(companyUUID)) {
            if (ret.settings?.targeting?.excludeUrls?.length > 0) {
              debug(
                'matchExactUrls formId=%s showIcon=%s matchUrlParts=%s',
                id,
                urlShow,
                matchUrlParts(pathname, hostname, paramseters, ret.settings?.targeting?.excludeUrls)
              );
              if (matchExactUrls(pathname, hostname, paramseters, ret.settings?.targeting?.excludeUrls)) {
                setUrlShow(false);
                return;
              }
            }
          }
          const [, includeUrls] = generageUrlFromPage(ret.settings?.targeting?.iconExcludePages);
          debug(
            'matchUrlParts formId=%s showIcon=%s matchUrlParts=%s',
            id,
            urlShow,
            matchUrlParts(pathname, hostname, paramseters, includeUrls)
          );
          if (matchUrlParts(pathname, hostname, paramseters, includeUrls)) {
            setUrlShow(false);
          } else {
            setUrlShow(true);
          }
        }
      });
    };

    if (urlShow) {
      handelPageChange();
    }
    window.addEventListener(PATH_CHANGE_EVENT, handelPageChange);

    return () => {
      window.removeEventListener(PATH_CHANGE_EVENT, handelPageChange);
    };
  }, [id, urlShow]);

  // 获取真实宽高
  useEffect(() => {
    if (!position?.iconUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.src = position.iconUrl;
    img.onload = function () {
      debug('icon比例: %s, %s, %s', img.width, img.height, img.width / img.height);
      setNaturalSize({
        width: img.width,
        height: img.height
      });
    };
  }, [position?.iconUrl, id]);

  useEffect(() => {
    if (ref.current) {
      cloneEl = ref.current.cloneNode(true);
    }
  }, [ref]);

  const handleDragStart = (event) => {
    //拖拽的元素 - 每次拖拽都重新创建克隆元素，确保样式是最新的
    if (ref.current) {
      cloneEl = ref.current.cloneNode(true);
    }
    mouseDown(event);
    return;
  };

  const handleDragEnd = (event) => {
    return;
  };

  const handleMouseStart = (event) => {
    //拖拽的元素
    let target = event.target;
    event = event.targetTouches[0] || event?.originalEvent?.changedTouches[0];
    elementTop = event.clientY - target.getBoundingClientRect().top;
    elementLeft = event.clientX - target.getBoundingClientRect().left;
  };

  const handleMouseMove = (event) => {
    if (!preventFlag) {
      preventFlag = true;
    }
    let css = JSON.parse(JSON.stringify(iconModtCss));
    //拖拽的元素
    let target = event.target;
    event = event.targetTouches[0] || event?.originalEvent?.changedTouches[0];
    //光标距离浏览器可视窗口边界的距离
    let boxLeft = event.clientX;
    let boxTop = event.clientY;
    css.top = boxTop - elementTop;
    css.left = boxLeft - elementLeft;
    css.bottom = '';
    css.right = '';

    if (css.top <= 0) {
      css.top = 0;
    } else if (css.top >= window.innerHeight - target.clientHeight) {
      css.top = window.innerHeight - target.clientHeight;
    } else if (css.left <= 0) {
      css.left = 0;
    } else if (css.left >= window.innerWidth - target.clientWidth) {
      css.left = window.innerWidth - target.clientWidth;
    }
    setIconModtCss({ ...css });
  };

  const handleMouseEnd = (event) => {
    if (!preventFlag) {
      return;
    }
    let css = JSON.parse(JSON.stringify(iconModtCss));
    //拖拽的元素
    let target = event.target;
    event = event.changedTouches[0] || event?.originalEvent?.changedTouches[0];
    let boxLeft = event.clientX;
    let boxTop = event.clientY;
    const dockInset = position?.iconClose
      ? calcIconCloseDockInset(
          boxLeft <= document.body.offsetWidth / 2 ? 'Left' : 'Right',
          closePosition,
          closeStyle,
          closeSize
        )
      : 0;
    if (boxLeft <= document.body.offsetWidth / 2) {
      css.left = dockInset ? `${dockInset}px` : 0;
      css.right = '';
    } else if (boxLeft > document.body.offsetWidth / 2) {
      css.right = dockInset ? `${dockInset}px` : 0;
      css.left = '';
    }
    if (boxTop <= elementTop) {
      css.top = 0;
      css.bottom = '';
    } else if (boxTop + target.clientHeight - elementTop >= window.innerHeight) {
      css.top = '';
      css.bottom = 0;
    } else if (boxTop > elementTop && boxTop + target.clientHeight - elementTop < window.innerHeight) {
      css.top = boxTop - elementTop;
      css.bottom = '';
    }
    setIconModtCss({ ...css });
    preventFlag = false;
  };

  const iconModtInit = () => {
    const positionArr = {
      LeftTop: { top: '100px', left: '0' },
      RightTop: { top: '100px', right: '0' },
      LeftMiddle: { top: '45%', left: '0' },
      RightMiddle: { top: '45%', right: '0' },
      LeftBottom: { bottom: '100px', left: '0' },
      RightBottom: { bottom: '100px', right: '0' }
    };

    // 浮层六位置贴边与线上一致；尺寸走 square/original 规则
    let css: any = {
      left: positionArr[position?.position]?.left || '',
      right: positionArr[position?.position]?.right || '',
      top: !position?.iconCustom && (positionArr[position?.position]?.top || ''),
      bottom: position?.iconCustom
        ? `${position.iconCustomBottom}${position.iconCustomBottomUnit || 'px'}`
        : positionArr[position?.position]?.bottom || '',
      backgroundColor: position?.backgroundColor || 'rgba(0, 0, 0, 0)',
      ...buildIconOpenSizeCss(boxSize, position?.iconFitMode)
    };
    if (position?.iconClose) {
      const inset = calcIconCloseDockInset(
        getIconDockSide(position?.position),
        closePosition,
        closeStyle,
        closeSize
      );
      // left/right 可能是 '0' / 0 / '0px'
      if (inset && (css.left === '0' || css.left === 0 || css.left === '0px')) {
        css.left = `${inset}px`;
      }
      if (inset && (css.right === '0' || css.right === 0 || css.right === '0px')) {
        css.right = `${inset}px`;
      }
    }
    if (!isOriginalFitMode(position?.iconFitMode) && closeEnhance) {
      css.overflow = 'visible';
    }
    setIconModtCss(css);
  };

  useEffect(() => {
    iconModtInit();
  }, [boxSize.width, boxSize.height, JSON.stringify(position)]);

  const clickShow = () => {
    setLocal(STORAGE_KEY.SHOW_FROM, 'click');
    setShow(true, 'click');
    formEventV2({
      event: 'float_ad_click',
      triggerEventType,
      formId: id,
      formName: name
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});
  };

  const mouseMoveCallback = (e) => {
    if (dragging && cloneEl) {
      const left = getNumber(ref.current.offsetLeft) + e.clientX - initial.x + 'px';
      const top = getNumber(ref.current.offsetTop) + e.clientY - initial.y + 'px';
      cloneEl.style.left = left;
      cloneEl.style.top = top;
    }
  };
  // 鼠标抬起
  const mouseUpCallback = (e) => {
    setTimeout(() => {
      end();
    }, 10);
  };
  // 鼠标离开了视窗
  const mouseLeaveCallback = (e) => {
    end();
  };
  // 用户可能离开了浏览器
  const blurCallback = () => {
    end();
  };

  const end = () => {
    dragging = false;
    if (!cloneEl) {
      return;
    }
    window.removeEventListener('mousemove', mouseMoveCallback);
    window.removeEventListener('mouseup', mouseUpCallback);
    window.removeEventListener('mouseleave', mouseLeaveCallback);
    window.removeEventListener('blur', blurCallback);

    let css = JSON.parse(JSON.stringify(iconModtCss));
    let boxLeft = getNumber(cloneEl.style.left) + cloneEl.clientWidth * 0.5;
    let boxTop = getNumber(cloneEl.style.top);
    let boxHeight = getNumber(cloneEl.style.height);
    // 左右吸附；inside 同侧时让出半个关闭钮
    const dockInset = position?.iconClose
      ? calcIconCloseDockInset(
          boxLeft <= document.body.offsetWidth / 2 ? 'Left' : 'Right',
          closePosition,
          closeStyle,
          closeSize
        )
      : 0;
    if (boxLeft <= document.body.offsetWidth / 2) {
      css.left = dockInset ? `${dockInset}px` : 0;
      css.right = '';
    } else if (boxLeft > document.body.offsetWidth / 2) {
      css.right = dockInset ? `${dockInset}px` : 0;
      css.left = '';
    }
    // 上下吸附
    if (boxTop <= 0) {
      css.top = 0;
      css.bottom = '';
    } else if (boxTop + boxHeight >= window.innerHeight) {
      css.top = '';
      css.bottom = 0;
    } else if (boxTop > 0 && boxTop + boxHeight < window.innerHeight) {
      css.top = boxTop + 'px';
      css.bottom = '';
    }
    setIconModtCss({ ...css });

    cloneEl && cloneEl.remove();
    // 清除闪动
    setTimeout(() => {
      ref.current.style.opacity = 1;
    }, 100);
  };

  const mouseDown = (e) => {
    if (!dragging && cloneEl) {
      dragging = true;

      // 确保克隆元素是最新的，重新创建并应用样式
      if (ref.current) {
        cloneEl = ref.current.cloneNode(true);
      }

      window.addEventListener('mousemove', mouseMoveCallback);
      window.addEventListener('mouseup', mouseUpCallback);
      window.addEventListener('mouseleave', mouseLeaveCallback);
      window.addEventListener('blur', blurCallback);

      ref.current.style.opacity = 0;
      cloneEl.style.opacity = 1;

      // 复制所有位置样式
      cloneEl.style.left = ref.current.style.left;
      cloneEl.style.right = ref.current.style.right;
      cloneEl.style.top = ref.current.style.top;
      cloneEl.style.bottom = ref.current.style.bottom;

      // 复制所有尺寸样式
      cloneEl.style.width = ref.current.style.width;
      cloneEl.style.height = ref.current.style.height;
      cloneEl.style.maxWidth = ref.current.style.maxWidth;
      cloneEl.style.maxHeight = ref.current.style.maxHeight;
      cloneEl.style.minWidth = ref.current.style.minWidth;
      cloneEl.style.minHeight = ref.current.style.minHeight;

      // 复制背景色等其他样式
      cloneEl.style.backgroundColor = ref.current.style.backgroundColor;

      // 复制内部图片的样式
      const cloneImg = cloneEl.querySelector('img');
      const refImg = ref.current.querySelector('img');
      if (cloneImg && refImg) {
        cloneImg.style.maxWidth = refImg.style.maxWidth;
        cloneImg.style.maxHeight = refImg.style.maxHeight;
        cloneImg.style.width = refImg.style.width;
        cloneImg.style.height = refImg.style.height;
      }

      document.body.appendChild(cloneEl);
      initial = { x: e.clientX, y: e.clientY };
    }
  };

  const handleIconClose = function (e) {
    e.preventDefault();
    e.stopPropagation();
    setUrlShow(false);
    setIconCloseTime(id);
    clearIconWinner();
    removeLocal(STORAGE_KEY.TEMP_POP);
  };

  // 如果position对象未完全加载，不渲染组件
  if (!position || !position.position) {
    return null;
  }

  const closeClassName = [
    'cartx-a-icon-close',
    closeEnhance ? `cartx-a-icon-close--${closePosition}` : '',
    closeEnhance ? `cartx-a-icon-close--${closeStyle}` : 'cartx-a-icon-close--legacy',
    closeEnhance ? 'cartx-b-close-preview-icon cartx-b-close-preview-icon--circle' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // 增强：弹窗同款 SVG；legacy：线上 close_custom
  const closeBtnStyle: React.CSSProperties = closeEnhance
    ? ({
        width: `${closeSize}px`,
        height: `${closeSize}px`,
        minWidth: `${closeSize}px`,
        minHeight: `${closeSize}px`,
        display: 'block',
        flexShrink: 0,
        cursor: 'pointer',
        boxSizing: 'border-box',
        '--close-btn-bg': closeBgColor || DEFAULT_ICON_CLOSE_BG_COLOR,
        '--close-btn-icon': closeColor || DEFAULT_ICON_CLOSE_COLOR,
        '--close-btn-size': closeSize
      } as React.CSSProperties)
    : {
        width: `${closeSize}px`,
        height: `${closeSize}px`,
        minWidth: `${closeSize}px`,
        minHeight: `${closeSize}px`,
        color: closeColor,
        fill: closeColor
      };

  const contentStyles = buildIconContentStyles(boxSize, position?.iconFitMode, {
    closeEnhance,
    iconPosition: position.position,
    iconSizeType: position?.iconSizeType,
    naturalWidth: naturalSize.width,
    naturalHeight: naturalSize.height
  });
  const CloseSvg = closeEnhance ? IconCloseSvg : CartxIcon;

  return (
    <div style={{ display: urlShow ? 'block' : 'none' }} className="cartx-a-open-holder cartx-a-open-holder-TL">
      <div className="cartx-a-open-extra-holder cartx-a-open-extra-holder-TL">
        <a
          ref={ref}
          className="cartx-a-open"
          style={{
            ...iconModtCss,
            opacity: iconModtCss.width ? 1 : 0,
            visibility: iconModtCss.width ? 'visible' : 'hidden',
            transition: iconModtCss.width ? 'opacity 0.2s ease-in' : 'none'
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          draggable="true"
          onClick={clickShow}
          onTouchStart={handleMouseStart}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseEnd}
        >
          <div
            className={`cartx-a-icon${closeStyle === 'inside' ? ' cartx-a-icon--close-inside' : ''}${
              closeEnhance ? ' cartx-a-icon--close-enhance' : ''
            }`}
            style={contentStyles.wrap as React.CSSProperties}
          >
            {!!(position.iconClose && urlShow && boxSize.width > 0) ? (
              <CloseSvg
                onClick={handleIconClose}
                className={closeClassName}
                style={closeBtnStyle}
                width={closeSize}
                height={closeSize}
              />
            ) : null}
            <img
              alt="icon"
              draggable="false"
              src={
                position && position.iconUrl
                  ? position.iconUrl
                  : `${getCompanyStaticUrl()}Public/cartsee/formImage/2e642a01175554bfaf5ab0b4f1c9cc03.gif`
              }
              style={contentStyles.img as React.CSSProperties}
              onLoad={(e) => {
                const el = e.currentTarget;
                if (el && el.naturalWidth && el.naturalHeight) {
                  setNaturalSize({
                    width: el.naturalWidth,
                    height: el.naturalHeight
                  });
                }
              }}
            />
          </div>
        </a>
      </div>
    </div>
  );
}

export default IconModt;
